import { ScrollView } from "react-native";
import { Button, IconButton, Text } from "react-native-paper";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { InsightCallout } from "../DataCards/InsightCallout";
import QuickStudyTabs from "./QuickStudyTabs";
import type { QuickStudyTab } from "./types";

type Props = {
	title?: string;
	onClose?: () => void;
	activeTab: QuickStudyTab;
	onChangeTab: (tab: QuickStudyTab) => void;
	recommendedLabel: string;
	customLabel: string;
	children: React.ReactNode;
	/** Description shown above the start button once a selection exists. */
	summaryText?: string;
	startLabel: string;
	startDisabled?: boolean;
	starting?: boolean;
	onStart: () => void;
	showStartButton?: boolean;
	showTabs?: boolean;
	scrollEnabled?: boolean;
};

const QuickStudyModal = ({
	title,
	onClose,
	activeTab,
	onChangeTab,
	recommendedLabel,
	customLabel,
	children,
	summaryText,
	startLabel,
	startDisabled,
	starting,
	onStart,
	showStartButton = true,
	showTabs = true,
	scrollEnabled = true,
}: Props) => {
	const theme = useAppTheme();
	const insets = useSafeAreaInsets();

	const { isMobile } = useDevice();

	const tabsHeader = showTabs ? (
		<Box paddingHorizontal={isMobile ? 16 : 24} paddingTop={16}>
			<QuickStudyTabs
				value={activeTab}
				onChange={onChangeTab}
				recommendedLabel={recommendedLabel}
				customLabel={customLabel}
			/>
		</Box>
	) : null;

	return (
		<Box
			flex={1}
			backgroundColor={theme.colors.surface}
			borderRadius={isMobile ? 0 : theme.roundness * 7}
			overflow="hidden"
		>
			{(title || onClose) && (
				<Box paddingHorizontal={24} paddingTop={16} alignItems="center">
					{title && (
						<Text
							variant="headlineSmall"
							style={{ color: theme.colors.onSurface }}
						>
							{title}
						</Text>
					)}
					{onClose && (
						<Box position="absolute" top={7} right={12}>
							<IconButton
								icon="close"
								size={24}
								iconColor={theme.colors.onSurfaceVariant}
								disabled={starting}
								onPress={onClose}
							/>
						</Box>
					)}
				</Box>
			)}

			{scrollEnabled ? (
				<Box flex={1} style={{ minHeight: 0 }}>
					{tabsHeader}
					<ScrollView
						showsVerticalScrollIndicator={false}
						contentContainerStyle={{
							paddingHorizontal: isMobile ? 16 : 24,
							paddingVertical: 16,
						}}
					>
						{children}
					</ScrollView>
				</Box>
			) : (
				<Box flex={1} style={{ minHeight: 0 }}>
					{tabsHeader}
					<Box flex={1} style={{ minHeight: 0, overflow: "hidden" }}>
						{children}
					</Box>
				</Box>
			)}

			{(summaryText || showStartButton) && (
				<Box
					paddingHorizontal={24}
					paddingTop={16}
					paddingBottom={16 + insets.bottom}
					gap={16}
					borderTopWidth={1}
					borderTopColor={theme.colors.surfaceDisabled}
				>
					{summaryText ? (
						<InsightCallout text={summaryText} variant="flat" />
					) : null}
					{showStartButton && (
						<Button
							mode="contained"
							icon="play-outline"
							onPress={onStart}
							disabled={startDisabled}
							loading={starting}
							contentStyle={{ height: 52 }}
							labelStyle={{ fontSize: 16 }}
							style={{ borderRadius: theme.roundness * 99 }}
						>
							{startLabel}
						</Button>
					)}
				</Box>
			)}
		</Box>
	);
};

export default QuickStudyModal;
